import { useState } from "react";

import { ErrorBox, Info24px } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Button, Tooltip } from "@similarweb/ui-components";
import { Text } from "@similarweb/ui-components/foundations";

import type { ChangeSolutionStepsType } from "app/components/ChangeSolutionModal/consts";
import { continueButtonText, skipPopupEventName } from "app/components/ChangeSolutionModal/consts";
import { trackEvents } from "app/components/ChangeSolutionModal/hooks/utils";
import { useTranslation } from "pro-features/components/WithTranslation";

import { ErrorContainer, StyledModalFooter, SkipButtonContainer, NextButtonStyled, ButtonsContainer } from "./styles";
import type { IUseCaseStepConfig, IStepConfig } from "./types";

export interface IChangeSolutionModalFooter {
    currentStep: ChangeSolutionStepsType;
    onBack: () => void;
    onNext: () => void;
    handleClose: () => Promise<void>;
    onComplete: () => Promise<void>;
    selectedSolution?: IUseCaseStepConfig;
    currentStepConfig: IStepConfig;
    isSwitchingSolution?: boolean;
    switchSolutionError?: Error | null;
    trackingEventName?: string;
}

const { red, blue, carbon } = colorsPalettes;

export const ChangeSolutionModalFooter = ({
    currentStep,
    currentStepConfig,
    onBack,
    onNext,
    handleClose,
    onComplete,
    selectedSolution,
    isSwitchingSolution,
    switchSolutionError,
    trackingEventName,
}: IChangeSolutionModalFooter) => {
    const translate = useTranslation();
    const [isSkipConfirmationMode, setIsSkipConfirmationMode] = useState(false);

    const { isLastStep, buttons: buttonsConfig } = currentStepConfig;

    const handleNext = () => {
        if (isLastStep) {
            void onComplete();
        } else {
            onNext();
        }
    };

    const handleSkip = (action: string, eventName: string, shouldSkipConfirmationMode: boolean) => {
        if (trackingEventName) {
            trackEvents(action, eventName);
        }
        setIsSkipConfirmationMode(shouldSkipConfirmationMode);
    };

    const handleSkipClick = () => {
        handleSkip("click", `${trackingEventName}/${skipPopupEventName}`, true);
    };

    const handleCancelSkip = () => {
        handleSkip("cancel", `${trackingEventName}/${skipPopupEventName}`, false);
    };

    const handleConfirmSkip = () => {
        handleSkip("confirm", `${trackingEventName}/${skipPopupEventName}`, false);
        void handleClose();
    };

    const nextButtonInitialState = buttonsConfig.nextButtonDisabled && !selectedSolution;

    const getContinueButtonText = () => {
        if (isSkipConfirmationMode) {
            return "components.shared.change_solution_modal.skip_confirmation_modal.confirm_button";
        }
        return switchSolutionError && isLastStep
            ? "components.shared.change_solution_modal.buttons.again"
            : continueButtonText[currentStep];
    };

    const getBackButtonText = () =>
        isSkipConfirmationMode
            ? "components.shared.change_solution_modal.skip_confirmation_modal.cancel_button"
            : "components.shared.change_solution_modal.buttons.back";

    const isNextButtonDisabled = !isSkipConfirmationMode && (nextButtonInitialState || isSwitchingSolution);

    return (
        <StyledModalFooter>
            {switchSolutionError && isLastStep ? (
                <ErrorContainer>
                    <ErrorBox
                        size={"sm"}
                        color={colorsPalettes.red[500]}
                    />
                    <Text
                        fontVariant="button"
                        color={red[500]}
                    >
                        {translate("components.shared.change_solution_modal.error.test")}
                    </Text>
                </ErrorContainer>
            ) : (
                buttonsConfig.showSkip && (
                    <SkipButtonContainer>
                        {!isSkipConfirmationMode ? (
                            <Button
                                onClick={handleSkipClick}
                                type="flat"
                            >
                                <Text
                                    fontVariant="button"
                                    color={blue[400]}
                                >
                                    {translate("components.shared.change_solution_modal.buttons.skip.and.stay")}
                                </Text>
                            </Button>
                        ) : (
                            <Text
                                fontVariant="button"
                                color={carbon[400]}
                            >
                                {translate("components.shared.change_solution_modal.buttons.skip")}
                            </Text>
                        )}
                        {isSkipConfirmationMode ? (
                            <Tooltip content={translate("switch.solution.skip.button.tooltip")}>
                                <Info24px
                                    color={colorsPalettes.carbon[200]}
                                    size="sm"
                                />
                            </Tooltip>
                        ) : null}
                    </SkipButtonContainer>
                )
            )}
            <ButtonsContainer>
                {buttonsConfig.showBack || isSkipConfirmationMode ? (
                    <Button
                        type="flat"
                        onClick={isSkipConfirmationMode ? handleCancelSkip : onBack}
                    >
                        {translate(getBackButtonText())}
                    </Button>
                ) : null}
                <NextButtonStyled
                    isDisabled={isNextButtonDisabled}
                    isLoading={isSwitchingSolution}
                    type="primary"
                    onClick={isSkipConfirmationMode ? handleConfirmSkip : handleNext}
                >
                    {translate(getContinueButtonText())}
                </NextButtonStyled>
            </ButtonsContainer>
        </StyledModalFooter>
    );
};
